<p-dialog
	header="Import files"
	[visible]="visible()"
	(visibleChange)="visible.set($event)"
	[modal]="true"
	[dismissableMask]="true"
	[style]="{ width: '52rem' }"
	[draggable]="false"
	[resizable]="false"
	appendTo="body"
	data-testid="import-dialog"
>
	<p-tabs [value]="activeTab()" (valueChange)="activeTab.set($any($event))">
		<p-tablist>
			<p-tab value="vehicle" data-testid="import-vehicle-tab">Vehicle</p-tab>
			<p-tab value="ilab" data-testid="import-ilab-tab">ILAB</p-tab>
			@if (rawImportEnabled()) {
				<p-tab value="raw" data-testid="import-raw-tab">Raw (zip / json)</p-tab>
			}
		</p-tablist>
		<p-tabpanels>
			<!-- Vehicle: xlsx/csv upload or manual card list, + optional ECU readout -->
			<p-tabpanel value="vehicle">
				<!-- Sub-mode: upload a file vs fill the manual grid -->
				<p-selectButton
					class="results-mode"
					[options]="resultsModeOptions"
					[ngModel]="resultsMode()"
					(ngModelChange)="resultsMode.set($event)"
					optionLabel="label"
					optionValue="value"
					[allowEmpty]="false"
					data-testid="import-results-mode-toggle"
				/>

				@if (resultsMode() === 'upload') {
					<!-- SOP for the upload: threaded to the backend via a file-name marker so an uploaded
               xlsx/csv is recorded under the chosen SOP. The test week is NOT selectable here — it
               is the source of truth from the file name's "TwXXXX_" prefix (validated below). -->
					<div class="manual-fields">
						<div class="manual-field">
							<span class="slot-title">SOP</span>
							<p-select
								[options]="sops()"
								[ngModel]="selectedSop()"
								(ngModelChange)="onSopChange($event)"
								optionLabel="name"
								placeholder="Select SOP"
								appendTo="body"
								[style]="{ width: '100%' }"
								data-testid="import-vehicle-sop-select"
							/>
						</div>
					</div>

					<!-- Excel results slot -->
					<label class="slot-field">
						<span class="slot-title">Excel results (.xlsx / .csv)</span>
						<div
							class="drop-zone"
							[class.drag-over]="dragOverResults()"
							[class.has-files]="selectedResults()"
							(dragover)="allowDrop($event); dragOverResults.set(true)"
							(dragleave)="dragOverResults.set(false)"
							(drop)="onResultsDrop($event)"
						>
							<input type="file" accept=".xlsx,.csv" hidden (change)="onResultsSelected($event)" data-testid="import-results-file-input" />
							@if (selectedResults()) {
								<div class="drop-zone-content-compact">
									<i class="pi pi-check-circle drop-icon-ok"></i>
									<span class="drop-file-name">{{ selectedResults()?.name }}</span>
									<i
										class="pi pi-times drop-file-remove"
										(click)="$event.preventDefault(); $event.stopPropagation(); clearResults()"
										(keydown.enter)="$event.preventDefault(); $event.stopPropagation(); clearResults()"
										tabindex="0"
										role="button"
										data-testid="import-results-remove-button"
									></i>
								</div>
							} @else {
								<div class="drop-zone-content">
									<p class="drop-text">
										<span class="select-link">Click to upload</span>
										or drag and drop
									</p>
									<small class="drop-hint">.xlsx / .csv</small>
								</div>
							}
						</div>
					</label>
					@if (uploadTestWeekInvalid()) {
						<small class="manual-ev-error">{{ uploadTestWeekHint }}</small>
					}
				} @else {
					<!-- Manual input: SOP + test week + configuration + grid -->
					<div class="manual-fields">
						<div class="manual-field">
							<span class="slot-title">SOP</span>
							<p-select
								[options]="sops()"
								[ngModel]="selectedSop()"
								(ngModelChange)="onSopChange($event)"
								optionLabel="name"
								placeholder="Select SOP"
								appendTo="body"
								[style]="{ width: '100%' }"
								data-testid="import-vehicle-sop-select"
							/>
						</div>
						<div class="manual-field">
							<span class="slot-title">Test week</span>
							<p-select
								[options]="testWeeks()"
								[ngModel]="selectedTestWeek()"
								(ngModelChange)="selectedTestWeek.set($event)"
								optionLabel="name"
								placeholder="Select test week"
								appendTo="body"
								[style]="{ width: '100%' }"
								data-testid="import-test-week-select"
							/>
						</div>
					</div>
					<div class="manual-field">
						<span class="slot-title">Configuration (vehicle name)</span>
						<input
							type="text"
							pInputText
							[ngModel]="configuration()"
							(ngModelChange)="configuration.set($event)"
							placeholder="e.g. Ouninpohja"
							[style]="{ width: '100%' }"
							data-testid="import-configuration-input"
						/>
					</div>

					<div class="manual-entries">
						@for (row of rows(); track $index; let i = $index) {
							<div class="manual-card" [class.collapsed]="expandedRow() !== i">
								<div class="manual-card-head" (click)="toggleRow(i)" (keydown.enter)="toggleRow(i)" tabindex="0" role="button" data-testid="import-manual-row-toggle">
									<i class="pi manual-card-chevron" [class.pi-chevron-down]="expandedRow() === i" [class.pi-chevron-right]="expandedRow() !== i"></i>
									<span class="manual-card-title">Entry {{ i + 1 }}</span>
									@if (expandedRow() !== i) {
										<span class="manual-card-summary" [class.is-nok]="row.result === 'NOK'">{{ rowSummary(row) }}</span>
									}
									<i
										class="pi pi-times manual-card-remove"
										(click)="$event.stopPropagation(); removeRow(i)"
										(keydown.enter)="$event.stopPropagation(); removeRow(i)"
										tabindex="0"
										role="button"
										data-testid="import-manual-row-remove-button"
									></i>
								</div>

								@if (expandedRow() === i) {
									<div class="manual-card-body">
										<div class="manual-card-field">
											<label [for]="'uf-select-' + i">UF</label>
											<p-select
												[inputId]="'uf-select-' + i"
												[options]="ufTree()"
												[ngModel]="row.uf"
												(ngModelChange)="onRowUfChange(i, $event)"
												optionLabel="label"
												placeholder="Select UF"
												[showClear]="true"
												[filter]="true"
												filterBy="label"
												appendTo="body"
												panelStyleClass="manual-card-panel"
												[disabled]="loadingTree() || !ufTree().length"
												[style]="{ width: '100%' }"
												data-testid="import-manual-row-uf-select"
											/>
										</div>

										<div class="manual-card-field">
											<label [for]="'uc-select-' + i">UC</label>
											<p-select
												[inputId]="'uc-select-' + i"
												[options]="ucOptions(row)"
												[ngModel]="row.uc"
												(ngModelChange)="onRowUcChange(i, $event)"
												optionLabel="label"
												placeholder="Select UC"
												[showClear]="true"
												[filter]="true"
												filterBy="label"
												appendTo="body"
												panelStyleClass="manual-card-panel"
												[disabled]="!row.uf"
												[style]="{ width: '100%' }"
												data-testid="import-manual-row-uc-select"
											/>
										</div>

										<div class="manual-card-field">
											<label [for]="'scn-select-' + i">SCN</label>
											<p-select
												[inputId]="'scn-select-' + i"
												[options]="scnOptions(row)"
												[ngModel]="row.scn"
												(ngModelChange)="setRow(i, { scn: $event })"
												optionLabel="label"
												placeholder="Select SCN"
												[showClear]="true"
												[filter]="true"
												filterBy="label"
												appendTo="body"
												panelStyleClass="manual-card-panel"
												[disabled]="!row.uf"
												[style]="{ width: '100%' }"
												data-testid="import-manual-row-scn-select"
											/>
										</div>

										<div class="manual-card-result-row">
											<div class="manual-card-field manual-card-ev">
												<label [for]="'ev-ticket-' + i">EV ticket</label>
												<input
													[id]="'ev-ticket-' + i"
													type="text"
													pInputText
													[ngModel]="row.ev"
													(ngModelChange)="setRow(i, { ev: $event })"
													placeholder="Separate multiple tickets with commas (e.g., EV-1234, EV-5678)"
													[style]="{ width: '100%' }"
													data-testid="import-manual-row-ev-input"
												/>
											</div>
											<div class="manual-card-field">
												<span [id]="'result-label-' + i">Result</span>
												<p-selectButton
													class="manual-result-toggle"
													[class.is-nok]="row.result === 'NOK'"
													[options]="resultOptions"
													[ngModel]="row.result"
													(ngModelChange)="setRow(i, { result: $event })"
													[allowEmpty]="false"
													[ariaLabelledBy]="'result-label-' + i"
													data-testid="import-manual-row-result-toggle"
												/>
											</div>
										</div>
									</div>
								}
							</div>
						}
						<button type="button" class="manual-add-row" (click)="addRow()" data-testid="import-manual-add-row-button">
							<i class="pi pi-plus"></i>
							Add entry
						</button>
						@if (hasInvalidEv()) {
							<small class="manual-ev-error">{{ jiraTicketHint }}</small>
						}
					</div>
				}

				<!-- ECU readout slot (optional) — shared by both sub-modes -->
				<label class="slot-field">
					<span class="slot-title">ECU readout (XCOM) — optional</span>
					<div
						class="drop-zone"
						[class.drag-over]="dragOverReadout()"
						[class.has-files]="selectedReadout()"
						(dragover)="allowDrop($event); dragOverReadout.set(true)"
						(dragleave)="dragOverReadout.set(false)"
						(drop)="onReadoutDrop($event)"
					>
						<input type="file" accept=".txt" hidden (change)="onReadoutSelected($event)" data-testid="import-readout-file-input" />
						@if (selectedReadout()) {
							<div class="drop-zone-content-compact">
								<i class="pi pi-check-circle drop-icon-ok"></i>
								<span class="drop-file-name">{{ selectedReadout()?.name }}</span>
								<i
									class="pi pi-times drop-file-remove"
									(click)="$event.preventDefault(); $event.stopPropagation(); clearReadout()"
									(keydown.enter)="$event.preventDefault(); $event.stopPropagation(); clearReadout()"
									tabindex="0"
									role="button"
									data-testid="import-readout-remove-button"
								></i>
							</div>
						} @else {
							<div class="drop-zone-content">
								<p class="drop-text">
									<span class="select-link">Click to upload</span>
									or drag and drop
								</p>
								<small class="drop-hint">.txt readout</small>
							</div>
						}
					</div>
				</label>

				<div class="import-dialog-actions">
					<p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="close()" [disabled]="busy()" data-testid="import-vehicle-cancel-button" />
					<p-button label="Import" (onClick)="submitResults()" [disabled]="!canImportResults()" [loading]="busy()" data-testid="import-vehicle-import-button" />
				</div>
			</p-tabpanel>

			<!-- ILAB / RESI import: one or more res.json + environment/SOP/suite/sub-suite,
           optional jsondiff (single result only) + ECU readout. -->
			<p-tabpanel value="ilab">
				<div class="manual-fields">
					<div class="manual-field">
						<span class="slot-title">Environment</span>
						<p-selectButton
							[options]="resiEnvironmentOptions"
							[ngModel]="resiEnvironment()"
							(ngModelChange)="resiEnvironment.set($event)"
							[allowEmpty]="false"
							data-testid="import-ilab-environment-toggle"
						/>
					</div>
					<div class="manual-field">
						<span class="slot-title">SOP</span>
						<p-select
							[options]="sops()"
							[ngModel]="resiSop()"
							(ngModelChange)="resiSop.set($event)"
							optionLabel="name"
							placeholder="Select SOP"
							appendTo="body"
							[style]="{ width: '100%' }"
							data-testid="import-ilab-sop-select"
						/>
					</div>
				</div>

				<div class="manual-fields">
					<div class="manual-field">
						<span class="slot-title">Suite</span>
						<input
							type="text"
							pInputText
							[ngModel]="resiSuite()"
							(ngModelChange)="resiSuite.set($event)"
							placeholder="e.g. Tw2618_Kilo_Bus"
							[style]="{ width: '100%' }"
							data-testid="import-ilab-suite-input"
						/>
					</div>
					@if (resiSingleResult()) {
						<div class="manual-field">
							<span class="slot-title">Sub suite (optional)</span>
							<input
								type="text"
								pInputText
								[ngModel]="resiSubSuite()"
								(ngModelChange)="resiSubSuite.set($event)"
								placeholder="defaults to the result's configuration"
								[style]="{ width: '100%' }"
								data-testid="import-ilab-sub-suite-input"
							/>
						</div>
					}
				</div>

				<!-- Result files (one or more res.json) — each becomes its own main suite sharing the suite name -->
				<label class="slot-field">
					<span class="slot-title">Result file(s) (res.json) — one or more</span>
					<div
						class="drop-zone"
						[class.drag-over]="dragOverResiResults()"
						[class.has-files]="selectedResiResults().length"
						(dragover)="allowDrop($event); dragOverResiResults.set(true)"
						(dragleave)="dragOverResiResults.set(false)"
						(drop)="onResiResultsDrop($event)"
					>
						<input type="file" accept=".json" multiple hidden (change)="onResiResultsSelected($event)" data-testid="import-ilab-results-file-input" />
						<div class="drop-zone-content">
							<p class="drop-text">
								<span class="select-link">Click to upload</span>
								or drag and drop
							</p>
							<small class="drop-hint">.json (one or more)</small>
						</div>
					</div>
				</label>
				@if (selectedResiResults().length) {
					<div class="resi-file-list">
						@for (f of selectedResiResults(); track i; let i = $index) {
							<div class="resi-file-row">
								<i class="pi pi-check-circle drop-icon-ok"></i>
								<span class="drop-file-name">{{ f.name }}</span>
								<i
									class="pi pi-times drop-file-remove"
									(click)="removeResiResult(i)"
									(keydown.enter)="removeResiResult(i)"
									tabindex="0"
									role="button"
									data-testid="import-ilab-result-remove-button"
								></i>
							</div>
						}
					</div>
				}

				<!-- Capture file (.jsondiff) — optional, only meaningful for a single result -->
				@if (resiSingleResult()) {
					<label class="slot-field">
						<span class="slot-title">Capture file (.jsondiff) — optional</span>
						<div
							class="drop-zone"
							[class.drag-over]="dragOverResiJsonDiff()"
							[class.has-files]="selectedResiJsonDiff()"
							(dragover)="allowDrop($event); dragOverResiJsonDiff.set(true)"
							(dragleave)="dragOverResiJsonDiff.set(false)"
							(drop)="onResiJsonDiffDrop($event)"
						>
							<input type="file" accept=".jsondiff" hidden (change)="onResiJsonDiffSelected($event)" data-testid="import-ilab-jsondiff-file-input" />
							@if (selectedResiJsonDiff()) {
								<div class="drop-zone-content-compact">
									<i class="pi pi-check-circle drop-icon-ok"></i>
									<span class="drop-file-name">{{ selectedResiJsonDiff()?.name }}</span>
									<i
										class="pi pi-times drop-file-remove"
										(click)="$event.preventDefault(); $event.stopPropagation(); clearResiJsonDiff()"
										(keydown.enter)="$event.preventDefault(); $event.stopPropagation(); clearResiJsonDiff()"
										tabindex="0"
										role="button"
										data-testid="import-ilab-jsondiff-remove-button"
									></i>
								</div>
							} @else {
								<div class="drop-zone-content">
									<p class="drop-text">
										<span class="select-link">Click to upload</span>
										or drag and drop
									</p>
									<small class="drop-hint">.jsondiff</small>
								</div>
							}
						</div>
					</label>
				}

				<!-- XCOM readout (all_dtc) or ILab readout (ecuid.json) — optional -->
				<label class="slot-field">
					<span class="slot-title">XCOM readout or ILab readout (ecuid.json) — optional</span>
					<div
						class="drop-zone"
						[class.drag-over]="dragOverResiEcuid()"
						[class.has-files]="selectedResiEcuid()"
						(dragover)="allowDrop($event); dragOverResiEcuid.set(true)"
						(dragleave)="dragOverResiEcuid.set(false)"
						(drop)="onResiEcuidDrop($event)"
					>
						<input type="file" accept=".json,.txt" hidden (change)="onResiEcuidSelected($event)" data-testid="import-ilab-ecuid-file-input" />
						@if (selectedResiEcuid()) {
							<div class="drop-zone-content-compact">
								<i class="pi pi-check-circle drop-icon-ok"></i>
								<span class="drop-file-name">{{ selectedResiEcuid()?.name }}</span>
								<i
									class="pi pi-times drop-file-remove"
									(click)="$event.preventDefault(); $event.stopPropagation(); clearResiEcuid()"
									(keydown.enter)="$event.preventDefault(); $event.stopPropagation(); clearResiEcuid()"
									tabindex="0"
									role="button"
									data-testid="import-ilab-ecuid-remove-button"
								></i>
							</div>
						} @else {
							<div class="drop-zone-content">
								<p class="drop-text">
									<span class="select-link">Click to upload</span>
									or drag and drop
								</p>
								<small class="drop-hint">.json / .txt</small>
							</div>
						}
					</div>
				</label>

				<div class="import-dialog-actions">
					<p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="close()" [disabled]="busy()" data-testid="import-ilab-cancel-button" />
					<p-button label="Import" (onClick)="submitResi()" [disabled]="!canImportResi()" [loading]="busy()" data-testid="import-ilab-import-button" />
				</div>
			</p-tabpanel>

			<!-- Raw passthrough: drop a full .zip (json + attachments) or a bare .json, imported as-is. -->
			@if (rawImportEnabled()) {
				<p-tabpanel value="raw">
					<label class="slot-field">
						<span class="slot-title">Import file (.zip / .json)</span>
						<div
							class="drop-zone"
							[class.drag-over]="dragOverRaw()"
							[class.has-files]="selectedRawFile()"
							(dragover)="allowDrop($event); dragOverRaw.set(true)"
							(dragleave)="dragOverRaw.set(false)"
							(drop)="onRawDrop($event)"
						>
							<input type="file" accept=".zip,.json" hidden (change)="onRawSelected($event)" data-testid="import-raw-file-input" />
							@if (selectedRawFile()) {
								<div class="drop-zone-content-compact">
									<i class="pi pi-check-circle drop-icon-ok"></i>
									<span class="drop-file-name">{{ selectedRawFile()?.name }}</span>
									<i
										class="pi pi-times drop-file-remove"
										(click)="$event.preventDefault(); $event.stopPropagation(); clearRaw()"
										(keydown.enter)="$event.preventDefault(); $event.stopPropagation(); clearRaw()"
										tabindex="0"
										role="button"
										data-testid="import-raw-remove-button"
									></i>
								</div>
							} @else {
								<div class="drop-zone-content">
									<p class="drop-text">
										<span class="select-link">Click to upload</span>
										or drag and drop
									</p>
									<small class="drop-hint">.zip / .json — imported as-is</small>
								</div>
							}
						</div>
					</label>

					<div class="import-dialog-actions">
						<p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="close()" [disabled]="busy()" data-testid="import-raw-cancel-button" />
						<p-button label="Import" (onClick)="submitRaw()" [disabled]="!canImportRaw()" [loading]="busy()" data-testid="import-raw-import-button" />
					</div>
				</p-tabpanel>
			}
		</p-tabpanels>
	</p-tabs>
</p-dialog>
